import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getDomainConfig } from '../config/domainConfig';

const Login = ({ onLogin }) => {
  const domainConfig = getDomainConfig();
  const { login } = useAuth();
  const [form, setForm] = useState({ tenant_code: domainConfig.tenant_code, email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [focused, setFocused] = useState('');
  const isBranded = !!domainConfig.tenant_code;
  const primary = domainConfig.primary_color || '#1B6B3A';
  const isCustomer = domainConfig.type === 'customer';

  useEffect(() => {
    document.title = domainConfig.app_name || 'FibraACS';
  }, []);

  const handleLogin = async () => {
    try {
      setLoading(true);
      setError('');
      const user = await login(form.tenant_code, form.email, form.password);
      onLogin(user);
    } catch (e) {
      setError(e.response?.data?.message || 'Invalid credentials');
    } finally { setLoading(false); }
  };

  const inputStyle = (field) => ({
    width: '100%', padding: '14px 16px',
    background: focused === field ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.04)',
    border: `1px solid ${focused === field ? primary : 'rgba(255,255,255,0.1)'}`,
    borderRadius: '12px', color: 'white', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box', transition: 'all 0.2s',
    fontFamily: 'inherit'
  });

  return (
    <div style={{
      minHeight: '100vh',
      background: '#060d0f',
      display: 'flex',
      fontFamily: "'Trebuchet MS', sans-serif",
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Background effects */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `radial-gradient(ellipse at 30% 40%, ${primary}1a 0%, transparent 60%),
                     radial-gradient(ellipse at 70% 70%, #00aaff0f 0%, transparent 50%)`,
      }} />
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `radial-gradient(${primary}15 1px, transparent 1px)`,
        backgroundSize: '24px 24px',
      }} />

      {/* Left branding panel */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        justifyContent: 'center', padding: '60px',
        position: 'relative', zIndex: 1
      }}>
        <div style={{ maxWidth: '500px' }}>
          <div style={{ marginBottom: '48px', display: 'flex', alignItems: 'center', gap: '16px' }}>
            {domainConfig.logo_url ? (
              <img src={domainConfig.logo_url} alt="logo"
                style={{ height: '52px', objectFit: 'contain' }} />
            ) : (
              <>
                <div style={{
                  width: '52px', height: '52px', borderRadius: '14px',
                  background: `linear-gradient(135deg, ${primary}, ${primary}88)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '26px', boxShadow: `0 12px 40px ${primary}44`
                }}>📡</div>
                <div>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: 'white', letterSpacing: '-0.5px' }}>
                    {domainConfig.app_name}
                  </div>
                  <div style={{ fontSize: '11px', color: '#ffffff33', letterSpacing: '3px', textTransform: 'uppercase' }}>
                    {isCustomer ? 'Self Service Portal' : 'Network Management'}
                  </div>
                </div>
              </>
            )}
          </div>

          {isCustomer ? (
            <>
              <h1 style={{ fontSize: '44px', fontWeight: '700', color: 'white', lineHeight: '1.1', marginBottom: '20px', letterSpacing: '-2px' }}>
                Manage Your<br/><span style={{ color: primary }}>WiFi</span><br/>Connection
              </h1>
              <p style={{ color: '#ffffff44', fontSize: '15px', lineHeight: '1.7', marginBottom: '40px' }}>
                Change your WiFi password, reboot your router, and monitor your connection — all from one place.
              </p>
              {['🔑 Change WiFi password instantly', '🔄 Reboot your router remotely', '📶 Enable or disable WiFi bands', '📊 View connection status'].map((f, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                  <span style={{ color: '#ffffff66', fontSize: '14px' }}>{f}</span>
                </div>
              ))}
            </>
          ) : (
            <>
              <h1 style={{ fontSize: '44px', fontWeight: '700', color: 'white', lineHeight: '1.1', marginBottom: '20px', letterSpacing: '-2px' }}>
                ONT Device<br/><span style={{ color: primary }}>Management</span><br/>Platform
              </h1>
              <p style={{ color: '#ffffff44', fontSize: '15px', lineHeight: '1.7', marginBottom: '40px' }}>
                Monitor optical signals, manage WiFi settings, and control customer ONTs across your entire network.
              </p>
              {[
                { icon: '📡', text: 'Real-time optical signal monitoring' },
                { icon: '🔑', text: 'Remote WiFi password management' },
                { icon: '⚡', text: 'TR-069 speed testing' },
                { icon: '🏢', text: 'Multi-tenant white label platform' },
              ].map((f, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '8px',
                    background: `${primary}22`, border: `1px solid ${primary}33`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px'
                  }}>{f.icon}</div>
                  <span style={{ color: '#ffffff66', fontSize: '14px' }}>{f.text}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Right login panel */}
      <div style={{
        width: '460px', display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '40px',
        position: 'relative', zIndex: 1
      }}>
        <div style={{
          width: '100%',
          background: 'rgba(255,255,255,0.03)',
          backdropFilter: 'blur(24px)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '24px', padding: '40px',
          boxShadow: '0 40px 80px rgba(0,0,0,0.6)'
        }}>
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ color: 'white', fontSize: '22px', fontWeight: '600', margin: '0 0 8px', letterSpacing: '-0.5px' }}>
              {isCustomer ? 'Access Your Account' : 'Welcome Back'}
            </h2>
            <p style={{ color: '#ffffff33', fontSize: '13px', margin: 0 }}>
              {isCustomer ? 'Use your registered phone number' : 'Sign in to continue'}
            </p>
          </div>

          {error && (
            <div style={{
              background: '#ff444414', border: '1px solid #ff444430',
              borderRadius: '10px', padding: '12px 16px',
              color: '#ff7777', fontSize: '13px', marginBottom: '20px'
            }}>⚠️ {error}</div>
          )}

          {!isBranded && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#ffffff44', fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>Tenant Code</label>
              <input type="text" placeholder={domainConfig.placeholder || "e.g. FIBRA"}
                value={form.tenant_code}
                onChange={e => setForm({ ...form, tenant_code: e.target.value.toUpperCase() })}
                onFocus={() => setFocused('tenant')} onBlur={() => setFocused('')}
                style={{ ...inputStyle('tenant'), fontFamily: 'monospace', letterSpacing: '2px' }} />
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label style={{ color: '#ffffff44', fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>
              {isCustomer ? 'Phone Number' : 'Email Address'}
            </label>
            <input
              type={isCustomer ? 'tel' : 'text'}
              placeholder={isCustomer ? 'Your registered phone' : 'Email or LCO/Sub ID (e.g. KB13A)'}
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              onFocus={() => setFocused('email')} onBlur={() => setFocused('')}
              style={inputStyle('email')} />
          </div>

          <div style={{ marginBottom: '28px' }}>
            <label style={{ color: '#ffffff44', fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>Password</label>
            <div style={{ position: 'relative' }}>
              <input type={showPass ? 'text' : 'password'} placeholder="Enter your password"
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                onFocus={() => setFocused('password')} onBlur={() => setFocused('')}
                onKeyPress={e => e.key === 'Enter' && handleLogin()}
                style={{ ...inputStyle('password'), paddingRight: '48px' }} />
              <button type="button" onClick={() => setShowPass(!showPass)}
                style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#ffffff66', fontSize: '16px', padding: '4px' }}>
                {showPass ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <button onClick={handleLogin} disabled={loading} style={{
            width: '100%', padding: '15px',
            background: loading ? 'rgba(255,255,255,0.08)' : `linear-gradient(135deg, ${primary} 0%, ${primary}cc 100%)`,
            border: 'none', borderRadius: '12px',
            color: 'white', fontSize: '15px', fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            boxShadow: loading ? 'none' : `0 8px 32px ${primary}40`,
            transition: 'all 0.3s'
          }}>
            {loading ? '⏳ Signing in...' : 'Sign In →'}
          </button>

          <div style={{ marginTop: '28px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
            <p style={{ color: '#ffffff1a', fontSize: '11px', margin: 0, letterSpacing: '1.5px', textTransform: 'uppercase' }}>
              {isBranded ? `© ${new Date().getFullYear()} ${domainConfig.app_name || 'FibraACS'} · All Rights Reserved` : `FibraACS v1.0 · Fibralink Technologies © ${new Date().getFullYear()}`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
